import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Edit2, Handshake, ChevronRight, ChevronDown, Trash2, X, Building2 } from "lucide-react";
import { Pill } from "@/components/badges";
import { toast } from "sonner";

export default function PartnershipsPage() {
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [editingCat, setEditingCat] = useState(null);
  const [showNewCat, setShowNewCat] = useState(false);
  const [partnerModal, setPartnerModal] = useState(null); // {category, partner?}
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/partnerships"), api.get("/partners")]);
    setCategories(c.data); setPartners(p.data);
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const map = {};
    partners.forEach((p) => { (map[p.category] = map[p.category] || []).push(p); });
    return map;
  }, [partners]);

  const toggle = (name) => {
    const s = new Set(expanded);
    if (s.has(name)) s.delete(name); else s.add(name);
    setExpanded(s);
  };

  const delPartner = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/partners/${confirmDelete.id}`);
      toast.success("Partner removed");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8" data-testid="partnerships-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="eyb-label">Configuration</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Partnerships</h1>
          <p className="text-sm text-slate-500 mt-1">Manage referral categories and the partner firms within each.</p>
        </div>
        <button data-testid="new-cat-btn" onClick={() => setShowNewCat(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold">
          <Plus className="w-4 h-4" /> New Category
        </button>
      </div>

      <div className="space-y-2">
        {categories.map((c) => {
          const items = grouped[c.name] || [];
          const isOpen = expanded.has(c.name);
          return (
            <div key={c.id} className="eyb-card overflow-hidden" data-testid={`cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <button
                onClick={() => toggle(c.name)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50/50"
              >
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  <div className="w-9 h-9 rounded-lg bg-[#29ABE2]/10 flex items-center justify-center text-[#1B4F8B]">
                    <Handshake className="w-4 h-4" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-slate-900">{c.name}</div>
                    <div className="text-[11px] text-slate-500">{items.length} partner firm{items.length === 1 ? "" : "s"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {c.active ? <Pill className="bg-emerald-50 text-emerald-700 border border-emerald-200">Active</Pill> : <Pill className="bg-slate-100 text-slate-600 border border-slate-200">Inactive</Pill>}
                  <span onClick={(e) => { e.stopPropagation(); setEditingCat(c); }} className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md cursor-pointer"><Edit2 className="w-3.5 h-3.5" /></span>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 bg-slate-50/30 px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="eyb-label">Partner firms in {c.name}</div>
                    <button
                      data-testid={`add-partner-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setPartnerModal({ category: c.name })}
                      className="text-xs font-semibold text-[#1f8ebf] hover:text-[#187199] inline-flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add partner
                    </button>
                  </div>
                  {items.length === 0 ? (
                    <div className="text-sm text-slate-400 py-3 text-center">No partner firms added yet.</div>
                  ) : (
                    <ul className="divide-y divide-slate-100 bg-white rounded-lg border border-slate-100">
                      {items.map((p) => (
                        <li key={p.id} className="px-3 py-2.5 flex items-center justify-between group" data-testid={`partner-${p.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-500">
                              <Building2 className="w-3.5 h-3.5" />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                              <div className="text-[11px] text-slate-500">
                                {[p.contact_name, p.email, p.phone].filter(Boolean).join(" · ") || "—"}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!p.active && <Pill className="bg-slate-100 text-slate-600 border border-slate-200">Inactive</Pill>}
                            <button onClick={() => setPartnerModal({ category: c.name, partner: p })} className="p-1.5 rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Edit2 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setConfirmDelete(p)} className="p-1.5 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(showNewCat || editingCat) && (
        <CategoryModal cat={editingCat} onClose={() => { setEditingCat(null); setShowNewCat(false); }} onSaved={() => { setEditingCat(null); setShowNewCat(false); load(); }} />
      )}
      {partnerModal && (
        <PartnerModal {...partnerModal} onClose={() => setPartnerModal(null)} onSaved={() => { setPartnerModal(null); load(); }} />
      )}
      {confirmDelete && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold mb-2">Remove partner?</h3>
            <p className="text-sm text-slate-600 mb-5">Remove <strong>{confirmDelete.name}</strong>? Leads already tagged with this partner are kept; the partner just won't be selectable for new leads.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
              <button onClick={delPartner} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-md">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryModal({ cat, onClose, onSaved }) {
  const [name, setName] = useState(cat?.name || "");
  const [active, setActive] = useState(cat?.active ?? true);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (cat) await api.patch(`/partnerships/${cat.id}`, { name, active });
      else await api.post("/partnerships", { name, active });
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">{cat ? "Edit category" : "New category"}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block"><span className="eyb-label">Category name</span>
            <input data-testid="cat-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
          </label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active</label>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button data-testid="submit-cat" type="submit" disabled={saving} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold rounded-full">{saving ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PartnerModal({ category, partner, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: partner?.name || "",
    contact_name: partner?.contact_name || "",
    email: partner?.email || "",
    phone: partner?.phone || "",
    active: partner?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, category };
      if (partner) await api.patch(`/partners/${partner.id}`, payload);
      else await api.post("/partners", payload);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="partner-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold">{partner ? "Edit partner" : "Add partner"}</h3>
            <div className="text-xs text-slate-500 mt-0.5">Category: <span className="font-semibold text-[#1f8ebf]">{category}</span></div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <label className="block"><span className="eyb-label">Firm / Partner name</span>
            <input data-testid="partner-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. ABC Accounting Pty Ltd" className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="eyb-label">Contact person</span>
              <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
            </label>
            <label className="block"><span className="eyb-label">Phone</span>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
            </label>
          </div>
          <label className="block"><span className="eyb-label">Email</span>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
          </label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button data-testid="submit-partner" type="submit" disabled={saving} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold rounded-full">{saving ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
